import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Users, MessageCircle, Send, Heart, HandHeart, Sprout, Dumbbell, Handshake, Loader2 } from 'lucide-react';
import BottomNav from '../components/BottomNav';
import CircleCommentSheet from '../components/CircleCommentSheet';
import { useAuth } from '../context/AuthContext';
import { IdentityToggle } from '../components/IdentityToggle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icons for the daily card type — matches the accent color of the circle.
const TYPE_META = {
  question:   { label: 'Question',   emoji: '❓' },
  exercise:   { label: 'Exercise',   emoji: '🧘' },
  discussion: { label: 'Discussion', emoji: '💬' },
};

// Reused branded reactions (same 5 as CommunityPage) — supportive, never
// competitive.
const REACTION_META = {
  love:    { icon: Heart,     colorOn: '#E76F51', label: 'Love This'       },
  needed:  { icon: HandHeart, colorOn: '#D6AD60', label: 'I Needed This'   },
  strong:  { icon: Dumbbell,  colorOn: '#AA8347', label: 'Stay Strong'     },
  growing: { icon: Sprout,    colorOn: '#79A87F', label: 'Growing'         },
  support: { icon: Handshake, colorOn: '#5B8DEF', label: 'Sending Support' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const initialsFromName = (n) => (n || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

const CirclePage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [draft, setDraft]       = useState('');
  const [postAnon, setPostAnon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [openComments, setOpenComments] = useState(null); // reflection id
  const [joined, setJoined] = useState(false);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detailRes, meRes] = await Promise.all([
        fetch(`${API_URL}/api/community/circles/${circleId}`),
        user ? fetch(`${API_URL}/api/community/me`, { headers: authHeaders() }) : Promise.resolve(null),
      ]);
      if (!detailRes.ok) {
        if (detailRes.status === 404) {
          toast.error('That circle doesn\u2019t exist yet.');
          navigate('/community', { replace: true });
          return;
        }
        throw new Error('load failed');
      }
      const body = await detailRes.json();
      setData(body);
      if (meRes && meRes.ok) {
        const me = await meRes.json();
        setJoined((me.circles || []).includes(circleId));
      }
    } catch {
      toast.error('Could not load the circle — please try again');
    } finally {
      setLoading(false);
    }
  }, [circleId, user, authHeaders, navigate]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  const shareToCircle = async () => {
    const text = draft.trim();
    if (!text) return;
    if (!user) { toast('Sign in to share your reflection'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/community/reflections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content: text, anonymous: postAnon, circle: circleId }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body.detail || 'Could not share — try again');
      } else {
        setData((prev) => ({ ...prev, reflections: [body.reflection, ...(prev?.reflections || [])] }));
        setDraft('');
        setPostAnon(false);
        toast.success('Shared with the circle 🌱');
      }
    } catch {
      toast.error('Network error — try again');
    } finally {
      setSubmitting(false);
    }
  };

  const react = async (id, reaction) => {
    if (!user) { toast('Sign in to encourage others'); return; }
    try {
      const res = await fetch(`${API_URL}/api/community/reflections/${id}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ reaction }),
      });
      const body = await res.json();
      if (res.ok) {
        setData((prev) => ({
          ...prev,
          reflections: (prev.reflections || []).map((r) => r.id === id ? body.reflection : r),
        }));
      }
    } catch { /* ignore */ }
  };

  const toggleJoin = async () => {
    if (!user) { toast('Sign in to join a circle'); return; }
    try {
      const res = await fetch(`${API_URL}/api/community/circles/${circleId}/toggle`, {
        method: 'POST', headers: authHeaders(),
      });
      const body = await res.json();
      if (res.ok) {
        setJoined(body.joined);
        toast.success(body.joined ? 'Joined the circle 💛' : 'Left the circle');
      }
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-24" style={{ background: 'linear-gradient(180deg, #1c3a33 0%, #0f1f1c 100%)' }}>
        <Loader2 className="w-6 h-6 animate-spin text-white/70" />
      </div>
    );
  }
  if (!data) return null;

  const { circle, today, todayDiscussion, pinnedDiscussion, reflections } = data;
  const accent = circle?.accent || '#D6AD60';
  const typeMeta = TYPE_META[today?.type] || TYPE_META.question;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(180deg, #1c3a33 0%, #0f1f1c 100%)' }} data-testid={`circle-page-${circleId}`}>
      {/* Header */}
      <div className="px-5 pt-4 pb-2">
        <Link
          to="/community"
          data-testid="circle-back-btn"
          className="inline-flex items-center gap-1.5 text-[11px] text-white/60 hover:text-white transition-colors mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Community
        </Link>
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-lg"
            style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 6px 22px ${accent}66` }}
          >
            {circle?.emoji}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em]" style={{ color: accent }}>
              Circle
            </p>
            <h1 className="text-2xl text-[#F8F6F0]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
              {circle?.title}
            </h1>
            <p className="text-[11px] text-white/55 mt-0.5 flex items-center gap-1">
              <Users className="w-3 h-3" /> {data.members} {data.members === 1 ? 'member' : 'members'}
            </p>
          </div>
          <button
            type="button"
            onClick={toggleJoin}
            data-testid="circle-join-btn"
            className="text-[10px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full transition-all active:scale-95"
            style={{
              background: joined ? accent : 'rgba(248,246,240,0.10)',
              color: joined ? '#242824' : '#F8F6F0',
              border: `1px solid ${joined ? accent : 'rgba(248,246,240,0.30)'}`,
            }}
          >
            {joined ? 'Joined' : 'Join'}
          </button>
        </div>
      </div>

      {/* Today's rotating card — the "interactive" heart of the circle */}
      {today && (
        <div className="px-5 mt-4" data-testid="circle-today-card">
          <div
            className="rounded-3xl p-5 shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${accent}22 0%, rgba(23,63,53,0.55) 100%)`,
              border: `1px solid ${accent}66`,
            }}
          >
            <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2" style={{ color: accent }}>
              <Sparkles className="w-3 h-3" /> Today&rsquo;s {typeMeta.label} · {typeMeta.emoji}
            </p>
            <h2
              className="text-[19px] leading-snug text-[#F8F6F0] mb-2"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
            >
              {today.title}
            </h2>
            <p className="text-[14px] leading-relaxed text-white/85 mb-4" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              {today.prompt}
            </p>

            {/* Response composer — inline so the interactive prompt has an
                obvious "reply here" affordance. */}
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                today.type === 'exercise'
                  ? 'When you\u2019ve done it, share how it felt…'
                  : today.type === 'question'
                    ? 'Your answer…'
                    : 'Add your voice…'
              }
              rows={2}
              maxLength={500}
              className="w-full px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed resize-none bg-white/95 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#D6AD60]/50"
              data-testid="circle-response-input"
            />
            <div className="mt-2 flex justify-start"><IdentityToggle anonymous={postAnon} onChange={setPostAnon} name={user?.displayName || user?.name} dark testId="circle-anonymous-toggle" /></div>
            <div className="flex items-center justify-between mt-2 gap-2">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-[10px] text-white/50 flex-shrink-0">{draft.length}/500</span>
              </div>
              <button
                type="button"
                onClick={shareToCircle}
                disabled={!draft.trim() || submitting}
                className="rbr-btn-gold px-5 py-2 text-[12px] flex-shrink-0"
                data-testid="circle-share-btn"
              >
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Post to circle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Today's Discussion — pinned conversation thread. Tapping "Join the
          conversation" opens the CircleCommentSheet on the synth reflection
          the backend created for (circle, date). Users can comment (with
          optional anonymous) directly on the discussion prompt without it
          getting lost in the general circle feed. */}
      {todayDiscussion && (
        <div className="px-5 mt-4" data-testid="circle-today-discussion">
          <div
            className="rounded-3xl p-5"
            style={{
              background: 'rgba(255,252,243,0.06)',
              border: `1px dashed ${accent}55`,
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: accent }}>
                <MessageCircle className="w-3 h-3" /> Today&rsquo;s Discussion · 💬
              </p>
              <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: `${accent}30`, color: accent }}>
                📌 Pinned
              </span>
            </div>
            <h2
              className="text-[18px] leading-snug text-[#F8F6F0] mb-2"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
            >
              {todayDiscussion.title}
            </h2>
            <p className="text-[13.5px] leading-relaxed text-white/80 mb-4" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              {todayDiscussion.prompt}
            </p>
            {pinnedDiscussion && (
              <button
                type="button"
                onClick={() => setOpenComments(pinnedDiscussion.id)}
                data-testid="circle-open-discussion-thread"
                className="w-full inline-flex items-center justify-between gap-2 px-4 py-3 rounded-2xl transition-all active:scale-[0.99]"
                style={{
                  background: '#FFFCF3',
                  border: `1px solid ${accent}55`,
                }}
              >
                <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-stone-800">
                  <MessageCircle className="w-4 h-4" style={{ color: accent }} />
                  {pinnedDiscussion.commentCount > 0
                    ? `Join the conversation — ${pinnedDiscussion.commentCount} ${pinnedDiscussion.commentCount === 1 ? 'reply' : 'replies'}`
                    : 'Start the conversation'}
                </span>
                <ArrowLeft className="w-4 h-4 rotate-180 text-stone-400" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Circle feed */}
      <div className="px-5 mt-6">
        <p className="text-[10px] uppercase tracking-[0.24em] font-semibold mb-3" style={{ color: accent }}>
          Circle Reflections
        </p>
        {reflections.length === 0 ? (
          <div className="text-center py-10 text-white/60 text-sm rounded-2xl bg-white/[0.03] border border-white/10">
            Nobody&rsquo;s shared in this circle yet. Be the first to open the door.
          </div>
        ) : (
          <div className="space-y-3">
            {reflections.map((r) => (
              <article
                key={r.id}
                className="rounded-2xl p-4"
                style={{ background: '#FFFCF3', border: `1px solid ${accent}33` }}
                data-testid={`circle-reflection-${r.id}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  {r.avatar ? (
                    <img src={r.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-[#242824]"
                         style={{ background: 'linear-gradient(135deg,#D6AD60 0%,#AA8347 100%)' }}>
                      {initialsFromName(r.name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-stone-800 truncate">{r.name}</p>
                    <p className="text-[10px] text-stone-400">{timeAgo(r.createdAt)}</p>
                  </div>
                </div>
                {r.imageUrl && <img src={`${API_URL}${r.imageUrl}`} alt="Shared journey postcard" className="w-full max-w-[260px] rounded-2xl mb-3 border border-stone-200" data-testid={`circle-post-image-${r.id}`} />}
                <p className="text-[14px] leading-relaxed text-stone-700 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  &ldquo;{r.content}&rdquo;
                </p>
                <div className="flex items-center gap-1 flex-wrap">
                  {Object.keys(REACTION_META).map((rk) => {
                    const meta = REACTION_META[rk];
                    const Icon = meta.icon;
                    const count = r.reactions?.[rk] || 0;
                    return (
                      <button
                        key={rk}
                        type="button"
                        onClick={() => react(r.id, rk)}
                        data-testid={`circle-react-${r.id}-${rk}`}
                        aria-label={meta.label}
                        title={meta.label}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-stone-50 hover:bg-stone-100 text-stone-600 border border-stone-200 transition-colors active:scale-95"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Icon className="w-3 h-3" style={{ color: count > 0 ? meta.colorOn : undefined }} />
                        {count}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setOpenComments(r.id)}
                    data-testid={`circle-open-comments-${r.id}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-stone-50 hover:bg-stone-100 text-stone-600 border border-stone-200 transition-colors active:scale-95"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <MessageCircle className="w-3 h-3" />
                    {r.commentCount || 0}
                    <span className="ml-0.5 text-[10px] uppercase tracking-wide text-stone-400">Reply</span>
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <CircleCommentSheet
        reflectionId={openComments}
        open={!!openComments}
        onClose={() => setOpenComments(null)}
        onCommentAdded={() => {
          // Bump the local commentCount for whichever reflection is open
          // (pinned discussion or a feed entry) without a full re-fetch.
          setData((prev) => {
            const bump = (r) => (r && r.id === openComments
              ? { ...r, commentCount: (r.commentCount || 0) + 1 }
              : r);
            return {
              ...prev,
              pinnedDiscussion: bump(prev?.pinnedDiscussion),
              reflections:      (prev?.reflections || []).map(bump),
            };
          });
        }}
      />

      <BottomNav />
    </div>
  );
};

export default CirclePage;
